import React, { useContext, useEffect, useState } from 'react';

import { app, media } from 'context';
import { Animate, Grid, Icon, Layout, Text } from 'ds';
import { actions } from 'store/OfficeRequest';
import { selectHeadCount, selectMaxMonthlyBudget } from 'store/OfficeRequest/selectors';
import { useAppDispatch, useAppSelector } from 'store/hooks';

import OfficeRequestBottomSheet from './OfficeRequestBottomSheet';
import OfficeRequestNumberStepper from './OfficeRequestNumberStepper';
import StepContainer from './StepContainer';
import StepDescription from './StepDescription';
import StepTitle from './StepTitle';
import { generateStepPath } from './utils';

interface Props {}

const Step5: React.FC<Props> = () => {
  const maxMonthlyBudget = useAppSelector(selectMaxMonthlyBudget);
  const headCount = useAppSelector(selectHeadCount);
  const dispatch = useAppDispatch();
  const { xs, sm } = useContext(media);
  const { isSmallPhone } = useContext(app);
  const isMobile = xs || sm;
  const [maxMonthlyBudgetOriginal, setMaxMonthlyBudgetOriginal] = useState<number>();

  const BudgetInput = (
    <OfficeRequestNumberStepper
      onChange={value => {
        dispatch(actions.setMaxMonthlyBudget(value || 0));
      }}
      value={maxMonthlyBudget}
      label="max monthly rent"
      max={10000000}
      stepValue={1000}
      isCurrency
    />
  );

  useEffect(() => {
    if (maxMonthlyBudget === undefined && headCount) {
      let budget = headCount * 1000;
      budget = Math.ceil(budget / 1000) * 1000;
      budget = Math.max(budget, 2000);

      setMaxMonthlyBudgetOriginal(budget);
      dispatch(actions.setMaxMonthlyBudget(budget));
    }
  }, [maxMonthlyBudget, headCount, dispatch]);

  return (
    <StepContainer
      step={5}
      backHref={generateStepPath({ step: 4 })}
      nextHref={generateStepPath({ step: 7 })}
      ctaDisabled={!maxMonthlyBudget}
      stepCompletedEventValue={{ maxMonthlyBudget, maxMonthlyBudgetOriginal }}
      {...(isSmallPhone ? { textLayoutProps: { marginTop: 32 } } : {})}
      stepName="budget"
      skippable
    >
      <Layout>
        <StepTitle>What's your max monthly rent budget?</StepTitle>
        {!isSmallPhone && (
          <Layout marginTop={isMobile ? 16 : 24}>
            <StepDescription>This helps us narrow in on fully serviced offices that fit your spend.</StepDescription>
          </Layout>
        )}
        {isMobile && (
          <Animate
            paddingY={24}
            marginTop={isSmallPhone ? 24 : 32}
            marginBottom={isSmallPhone ? 280 : 180}
            borderRadius={16}
            paddingX={28}
            background="rgba(236, 239, 241, 0.10)"
          >
            <Text size="body2" color="white" semibold>
              Launch your turnkey office
            </Text>
            {!isSmallPhone && (
              <Layout marginTop={4}>
                <Text size="body2" color="white">
                  After signing your lease, use Codi to streamline your office setup and logistics — saving you hundreds
                  of hours in admin tasks.
                </Text>
              </Layout>
            )}
            <Layout {...(isSmallPhone ? { marginTop: 12 } : { marginTop: 24 })}>
              <Grid itemsPerRow={2} gapY={16}>
                <Layout align="center">
                  <Icon name="sparkle" size="md" color="white" />
                  <Layout marginLeft={12}>
                    <Text size="body3" color="white">
                      AI office assistant
                    </Text>
                  </Layout>
                </Layout>
                <Layout align="center">
                  <Icon name="chair" size="md" color="white" />
                  <Layout marginLeft={12}>
                    <Text size="body3" color="white">
                      Furniture & design
                    </Text>
                  </Layout>
                </Layout>
                <Layout align="center">
                  <Layout transform="rotate(180deg)" display="inline-flex">
                    <Icon name="featherDuster" size="md" color="white" />
                  </Layout>
                  <Layout marginLeft={12}>
                    <Text size="body3" color="white">
                      Cleaning
                    </Text>
                  </Layout>
                </Layout>
                <Layout align="center">
                  <Icon name="beverageSupplies" size="md" color="white" />
                  <Layout marginLeft={12}>
                    <Text size="body3" color="white">
                      Pantry restocking
                    </Text>
                  </Layout>
                </Layout>
                <Layout align="center">
                  <Icon name="wifi" size="md" color="white" />
                  <Layout marginLeft={12}>
                    <Text size="body3" color="white">
                      Internet & IT
                    </Text>
                  </Layout>
                </Layout>
              </Grid>
            </Layout>
          </Animate>
        )}
      </Layout>
      {isMobile ? (
        <OfficeRequestBottomSheet>
          <Layout justify="center" paddingBottom={isSmallPhone ? 36 : 48}>
            {BudgetInput}
          </Layout>
        </OfficeRequestBottomSheet>
      ) : (
        <Animate>
          <Layout justify="center">{BudgetInput}</Layout>
          <Layout
            color="blue-25"
            paddingTop={24}
            marginTop={48}
            paddingBottom={12}
            borderRadius={8}
            direction="column"
            paddingX={16}
            maxWidth={524}
          >
            <Layout direction="column" align="center" gap={4}>
              <Text size="body-sm" semibold>
                Launch your turnkey office
              </Text>
              <Text size="body-xs" align="center">
                After signing your lease, use Codi to streamline your office setup and logistics — saving you hundreds
                of hours in admin tasks.
              </Text>
            </Layout>
            <Layout justify="space-between">
              <Layout borderRadius={8} paddingY={24} gap={8} align="center" direction="column" equalWidth>
                <Icon name="sparkle" size={24} />
                <Text size="body-xs" align="center">
                  AI office assistant
                </Text>
              </Layout>
              <Layout borderRadius={8} direction="column" align="center" paddingY={24} gap={8} equalWidth>
                <Icon name="chair" size={24} />
                <Text size="body-xs" align="center">
                  Furniture & design
                </Text>
              </Layout>
              <Layout borderRadius={8} direction="column" align="center" paddingY={24} gap={8} equalWidth>
                <Layout transform="rotate(180deg)" display="inline-flex">
                  <Icon name="featherDuster" size={24} />
                </Layout>
                <Text size="body-xs" align="center">
                  Cleaning
                </Text>
              </Layout>
              <Layout borderRadius={8} direction="column" align="center" paddingY={24} gap={8} equalWidth>
                <Icon name="beverageSupplies" size={24} />
                <Text size="body-xs" align="center">
                  Pantry restocking
                </Text>
              </Layout>
              <Layout borderRadius={8} direction="column" align="center" paddingY={24} gap={8} equalWidth>
                <Icon name="wifi" size={24} />
                <Text size="body-xs">Internet & IT</Text>
              </Layout>
            </Layout>
          </Layout>
        </Animate>
      )}
    </StepContainer>
  );
};

export default Step5;
